import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import type { DeliveryDashboard, ProposalDashboard } from '../../api/types'
import { Card, Empty, ErrorBox, Num, Spinner, Badge, plural } from '../../ui/primitives'
import { RecipientPanel } from './RecipientPanel'

/**
 * Indicatori di sintesi su proposte e consegne.
 *
 * Una nota che vale la pena tenere a mente leggendo la puntualità: nel sistema precedente
 * l'esito consegna aveva "non consegnato" come valore predefinito su centinaia di migliaia di
 * righe mai compilate, e la percentuale che ne usciva era sistematicamente pessimistica.
 * Qui la puntualità è calcolata solo sulle consegne con esito effettivamente dichiarato,
 * e il numero di quelle dichiarate è mostrato accanto: senza, la percentuale non è leggibile.
 */
export function DashboardPage() {
  const { selected, storeFilter, storesReady } = useStores()

  const [proposals, setProposals] = useState<ProposalDashboard>()
  const [deliveries, setDeliveries] = useState<DeliveryDashboard>()
  const [loadingProposals, setLoadingProposals] = useState(true)
  const [loadingDeliveries, setLoadingDeliveries] = useState(true)
  const [proposalsError, setProposalsError] = useState<unknown>()
  const [deliveriesError, setDeliveriesError] = useState<unknown>()

  // Ogni scheda carica per conto suo. Il quadro delle consegne interroga il gestionale su tutte
  // le consegne del periodo e su molti punti vendita arriva a decine di secondi: legandole
  // insieme, la pagina resterebbe bianca fino all'ultima risposta anche se le proposte sono
  // pronte in un attimo.
  // Finché l'elenco dei punti vendita non è arrivato il filtro non è affidabile — partire
  // adesso significherebbe chiedere con i codici per esteso, che il server rifiuta.
  const loadProposals = useCallback(() => {
    if (!storesReady) return
    setLoadingProposals(true)
    setProposalsError(undefined)
    api
      .get<ProposalDashboard>('/dashboard/proposals', { storeCode: storeFilter })
      .then(setProposals)
      .catch(setProposalsError)
      .finally(() => setLoadingProposals(false))
  }, [storeFilter, storesReady])

  const loadDeliveries = useCallback(() => {
    if (!storesReady) return
    setLoadingDeliveries(true)
    setDeliveriesError(undefined)
    api
      .get<DeliveryDashboard>('/dashboard/deliveries', { storeCode: storeFilter })
      .then(setDeliveries)
      .catch(setDeliveriesError)
      .finally(() => setLoadingDeliveries(false))
  }, [storeFilter, storesReady])

  useEffect(loadProposals, [loadProposals])
  useEffect(loadDeliveries, [loadDeliveries])

  const punctuality =
    deliveries && deliveries.declaredOutcomes > 0
      ? Math.round((deliveries.onTimeOutcomes / deliveries.declaredOutcomes) * 100)
      : undefined

  return (
    <>
      <div className="page-head">
        <h1>Dashboard</h1>
        <div className="page-tools">
          <span className="muted">
            {selected.length === 0 ?
              'Tutti i punti vendita'
            : plural(selected.length, 'punto vendita', 'punti vendita')}
          </span>
        </div>
      </div>

      <Card title="Proposte d'ordine">
        {loadingProposals ?
          <Spinner />
        : proposalsError ?
          <ErrorBox error={proposalsError} onRetry={loadProposals} />
        : <>
        <div className="grid grid-4">
          <Stat label="Aperte" value={<Num value={proposals?.openCount} />} />
          <Stat label="Righe da lavorare" value={<Num value={proposals?.openLines} />} />
          <Stat label="Confermate" value={<Num value={proposals?.confirmedCount} />} />
          <Stat label="Righe confermate" value={<Num value={proposals?.confirmedLines} />} />
        </div>

        {/* Qui c'era una classifica «fornitori principali». Diceva una cosa falsa: le proposte
            verso un centro di distribuzione non hanno un fornitore, e portano tutte il codice
            convenzionale 000000, per cui la tabella elencava «000000 CEDI NORD» e «000000
            MAGAZZINO CASTEL ROMANO» come se fossero due fornitori omonimi. La domanda a cui
            serviva a rispondere — dove è rimasto lavoro — la risolve l'istogramma qui sotto,
            che distingue fornitori e centri di distribuzione e si apre sul dettaglio. */}

        {proposals?.openCount === 0 && <Empty>Nessuna proposta aperta per i punti vendita selezionati.</Empty>}
          </>
        }
      </Card>

      <RecipientPanel />

      <Card title="Consegne">
        {loadingDeliveries ?
          <Spinner label="Caricamento consegne dal gestionale…" />
        : deliveriesError ?
          <ErrorBox error={deliveriesError} onRetry={loadDeliveries} />
        : <>
        <div className="grid grid-4">
          <Stat label="Totale nel periodo" value={<Num value={deliveries?.total} />} />
          <Stat
            label="In ritardo"
            value={<Num value={deliveries?.overdue} />}
            tone={deliveries && deliveries.overdue > 0 ? 'danger' : undefined}
          />
          <Stat label="Documento mancante" value={<Num value={deliveries?.missingDocument} />} />
          <Stat label="Da controllare" value={<Num value={deliveries?.awaitingReview} />} />
        </div>

        <div className="grid grid-2" style={{ marginTop: 16 }}>
          <div>
            <h3 style={{ marginBottom: 8 }}>Avanzamento</h3>
            {deliveries && (
              <table>
                <tbody>
                  {Object.entries(deliveries.byStatus).map(([status, count]) => (
                    <tr key={status}>
                      <td>
                        <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
                      </td>
                      <td className="num">
                        <Num value={count} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 style={{ marginBottom: 8 }}>Puntualità</h3>
            {punctuality === undefined ? (
              <Empty>
                Nessuna consegna con esito dichiarato nel periodo: la puntualità non è calcolabile.
              </Empty>
            ) : (
              <>
                <div className="stat-value">{punctuality}%</div>
                <div className="stat-note">
                  su <Num value={deliveries?.declaredOutcomes} /> consegne con esito dichiarato
                  {deliveries && deliveries.total > deliveries.declaredOutcomes && (
                    <>
                      {' '}(le altre <Num value={deliveries.total - deliveries.declaredOutcomes} /> non hanno
                      un esito registrato e non entrano nel calcolo)
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {deliveries && deliveries.aging.some((a) => a.count > 0) && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Scostamento dalla data attesa</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fascia</th>
                    <th className="num">Consegne</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.aging
                    .filter((a) => a.count > 0)
                    .map((a) => (
                      <tr key={a.daysThreshold}>
                        <td>{agingLabel(a.daysThreshold)}</td>
                        <td className="num">
                          <Num value={a.count} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {deliveries && deliveries.topSuppliers.length > 0 && (
          <div className="table-wrap" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Fornitori con più consegne in ritardo</h3>
            <table>
              <thead>
                <tr>
                  <th>Fornitore</th>
                  <th className="num">Consegne</th>
                  <th className="num">In ritardo</th>
                  <th className="num">Esiti dichiarati</th>
                  <th className="num">Puntuali</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.topSuppliers.map((s) => (
                  <tr key={`${s.supplierCode}~${s.supplierName}`}>
                    <td>
                      <span className="muted">{s.supplierCode}</span> {s.supplierName}
                    </td>
                    <td className="num">
                      <Num value={s.total} />
                    </td>
                    <td className="num">
                      {s.overdue > 0 ? <Badge tone="danger">{s.overdue}</Badge> : <span className="muted">—</span>}
                    </td>
                    <td className="num">
                      <Num value={s.declared} />
                    </td>
                    <td className="num">
                      {s.declared > 0 ? `${Math.round((s.onTime / s.declared) * 100)}%` : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        }
      </Card>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'danger' }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={tone === 'danger' ? { color: 'var(--danger)' } : undefined}>
        {value}
      </div>
    </div>
  )
}

function statusLabel(status: string) {
  switch (status) {
    case 'ordered':
      return 'Ordine confermato'
    case 'shipmentNotified':
      return 'Spedizione annunciata'
    case 'documentReceived':
      return 'Documento ricevuto'
    case 'completed':
      return 'Completata'
    default:
      return status
  }
}

function statusTone(status: string) {
  switch (status) {
    case 'shipmentNotified':
      return 'info' as const
    case 'documentReceived':
      return 'warn' as const
    case 'completed':
      return 'ok' as const
    default:
      return 'neutral' as const
  }
}

function agingLabel(threshold: number) {
  if (threshold < 0) return `Oltre ${Math.abs(threshold)} giorni di ritardo`
  if (threshold === 0) return 'In scadenza oggi'
  return `Entro ${threshold} giorni`
}
